/**
 * Приводит `width` и `minWidth` столбцов к числам.
 *
 * # Почему это обязательно
 *
 * В разметке ширины задаются атрибутом без двоеточия — `width="92"`,
 * `min-width="80"`, — поэтому в DevExtreme они приезжают СТРОКАМИ. Разметка от
 * этого не ломается, а вот арифметика изменения ширины ломается полностью.
 *
 * DevExtreme, упирая столбец в его минимум, считает ширину соседа так
 * (`m_columns_resizing_reordering.js`, режим `nextColumn`):
 *
 *     nextCellWidth = resizingInfo.nextColumnWidth - (delta + minWidth - cellWidth)
 *
 * `delta` — число (сдвиг мыши, при движении влево отрицательный), `minWidth` — наш
 * строковый «80». `delta + minWidth` для JS — это не сложение, а СКЛЕЙКА строк:
 * −600 и «80» дают «−60080». Дальше «−60080» участвует в вычитании уже как число,
 * и сосед получает ширину в десятки тысяч пикселей.
 *
 * Замер на стенде с раскладкой проценки: тянем правую границу «Наличия» влево до
 * упора — «Город» получает 53696 px вместо 100 px. Со стороны это ровно то самое
 * «столбец справа резко стал огромным». Ширины числами — «Город» получает 100 px,
 * то есть ровно те 8 px, что отдал сосед.
 *
 * Условие «упереть столбец в минимум» выполняется как раз при движении до упора,
 * поэтому дефект и ловился не всегда, а при сильном перетаскивании; а строковые
 * ширины есть почти в каждом гриде кабинета, поэтому «частенько и в разных
 * таблицах».
 *
 * Числовую строку переводим в число, всё остальное («auto», «50%», «92px») не
 * трогаем: это осмысленные значения вёрстки, а строковая арифметика ломается
 * только на числах.
 */

const NUMERIC = /^\d+(\.\d+)?$/;

function asNumber(value: unknown): number | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!NUMERIC.test(trimmed)) return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Мутирует переданные столбцы: именно этого ждёт `customizeColumns` DevExtreme.
 *
 * Принимаем `unknown` и разбираем на месте: столбцы описываются в каждом гриде своим
 * типом (у некоторых — только те поля, которые этот грид правит), и общий узкий тип
 * параметра отказывался с ними стыковаться, хотя ширины есть у всех.
 */
export function coerceNumericColumnWidths(columns: unknown): void {
  if (!Array.isArray(columns)) return;

  for (const item of columns) {
    if (item === null || typeof item !== "object") continue;

    const column = item as { width?: unknown; minWidth?: unknown };

    const width = asNumber(column.width);
    if (width !== null) column.width = width;

    const minWidth = asNumber(column.minWidth);
    if (minWidth !== null) column.minWidth = minWidth;
  }
}
